import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import type { TicketTypeList } from "@/types/init";
import { getPassengerCountKey } from "@/utils/passenger-type";
import { Minus, Plus, Users, X } from "lucide-react";
import { Fragment, useState } from "react";

const FIELD_CLASS =
  "flex h-12 w-full items-center justify-between rounded-2xl border-0 bg-primary-soft px-4 py-3.5 text-sm font-semibold text-foreground hover:bg-primary-soft";

export type PaxCount = {
  adultMYS: number;
  childMYS: number;
  infantMYS: number;
  adultForeign: number;
  childForeign: number;
  infantForeign: number;
  local: number;
  foc: number;
  worker: number;
};

type PaxRow = {
  key: keyof PaxCount;
  label: string;
  plural: string;
  ageFrom: number;
  ageTo: number;
  category: string;
};

type PaxGroup = {
  category: string;
  rows: PaxRow[];
};

const PAX_ORDER: (keyof PaxCount)[] = [
  "adultMYS",
  "adultForeign",
  "childMYS",
  "childForeign",
  "infantMYS",
  "infantForeign",
  "foc",
  "worker",
  "local",
];

// Malaysian, then Foreign, then any other category last.
const CATEGORY_ORDER = ["Malaysian", "Foreigner"];

const createPaxRows = (ticketTypeList: TicketTypeList[]): PaxRow[] => {
  const seen = new Set<keyof PaxCount>();
  const rows: PaxRow[] = [];

  for (const ticket of ticketTypeList) {
    const key = getPassengerCountKey(ticket);

    if (!key || seen.has(key)) {
      continue;
    }

    // Hide any category when it has no price
    if (
      ticket.unitPrice === 0 &&
      !ticket.ticketTypeName.trim().toLowerCase().includes("infant")
    ) {
      continue;
    }

    seen.add(key);

    rows.push({
      key,
      label: ticket.ticketTypeName,
      plural: `${ticket.ticketTypeName}s`,
      ageFrom: ticket.ageFrom,
      ageTo: ticket.ageTo,
      category: ticket.categoryName,
    });
  }

  return rows.sort(
    (a, b) => PAX_ORDER.indexOf(a.key) - PAX_ORDER.indexOf(b.key),
  );
};

const groupPaxRows = (rows: PaxRow[]): PaxGroup[] => {
  const groups: PaxGroup[] = [];

  rows.forEach((row) => {
    const group = groups.find((item) => item.category === row.category);

    if (group) {
      group.rows.push(row);
    } else {
      groups.push({ category: row.category, rows: [row] });
    }
  });

  return groups.sort((a, b) => {
    const rank = (category: string) => {
      const index = CATEGORY_ORDER.indexOf(category);
      return index === -1 ? CATEGORY_ORDER.length : index;
    };

    return rank(a.category) - rank(b.category);
  });
};

type PaxSelectProps = {
  label?: string;
  value: PaxCount;
  ticketTypeList: TicketTypeList[];
  onChange: (value: PaxCount) => void;
};

type StepperRowProps = {
  label: string;
  ageFrom: number;
  ageTo: number;
  value: number;
  min: number;
  max?: number;
  disableIncrement: boolean;
  onChange: (value: number) => void;
  onDecrement: () => void;
  onIncrement: () => void;
};

function StepperRow({
  label,
  ageFrom,
  ageTo,
  value,
  min,
  max,
  disableIncrement,
  onChange,
  onDecrement,
  onIncrement,
}: StepperRowProps) {
  const [isInputEmpty, setIsInputEmpty] = useState(false);

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-muted-foreground">
          {label}
        </span>
        {(ageFrom !== 0 || ageTo !== 0) && (
          <span className="text-xs text-muted">
            Age {ageTo >= 99 ? `${ageFrom}+` : `${ageFrom}-${ageTo}`}
          </span>
        )}
      </div>

      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="outline"
          className="aspect-square h-10 w-10 rounded-full border-primary bg-card p-0"
          onClick={onDecrement}
          disabled={value <= min}
          aria-label={`Decrease ${label}`}
        >
          <Minus size={18} className="text-primary" aria-hidden="true" />
        </Button>

        <input
          type="text"
          min={min}
          max={max}
          value={isInputEmpty && value === min ? "" : value}
          onChange={(event) => {
            const nextValue = event.target.value;

            if (nextValue === "") {
              setIsInputEmpty(true);
              onChange(min);
              return;
            }

            const next = Number(nextValue);

            if (Number.isNaN(next)) return;

            const clamped =
              max === undefined
                ? Math.max(min, next)
                : Math.min(max, Math.max(min, next));

            setIsInputEmpty(false);
            onChange(clamped);
          }}
          onBlur={() => {
            setIsInputEmpty(false);
          }}
          className="w-12 appearance-none bg-transparent text-center text-base font-bold text-foreground outline-none"
          aria-label={`${label} count`}
        />

        <Button
          type="button"
          variant="outline"
          className="aspect-square h-10 w-10 rounded-full border-primary bg-card p-0"
          onClick={onIncrement}
          disabled={disableIncrement}
          aria-label={`Increase ${label}`}
        >
          <Plus size={18} className="text-primary" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

export function PaxSelect({
  label = "Passengers",
  value,
  ticketTypeList,
  onChange,
}: PaxSelectProps) {
  const isMobile = useMobile();
  const [open, setOpen] = useState(false);
  const perTypeLimit = 999;

  const paxRows = createPaxRows(ticketTypeList);
  const paxGroups = groupPaxRows(paxRows);

  const [draft, setDraft] = useState<PaxCount>(value);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(value);
    }

    setOpen(next);
  };

  const update = (key: keyof PaxCount, next: number) => {
    setDraft((prev) => {
      const updated = {
        ...prev,
        [key]: Math.min(perTypeLimit, Math.max(0, next)),
      };

      onChange(updated);

      return updated;
    });
  };

  const handleDone = () => {
    onChange(draft);
    setOpen(false);
  };

  const hasSelection = paxRows.some((row) => value[row.key] > 0);

  const summary =
    paxRows
      .filter((row) => value[row.key] > 0)
      .map((row) => {
        const count = value[row.key];

        return `${count} ${count === 1 ? row.label : row.plural}`;
      })
      .join(", ") || "Select passengers";

  const trigger = (
    <Button type="button" className={FIELD_CLASS}>
      <span
        className={cn(
          "truncate text-sm font-medium",
          hasSelection ? "text-foreground" : "text-border",
        )}
      >
        {summary}
      </span>

      <Users size={16} aria-hidden="true" />
    </Button>
  );

  const stepper = (
    <div className="flex flex-col gap-4">
      {/* Scrollable passenger list */}
      <div className="max-h-[50vh] overflow-y-auto pr-2">
        <div className="flex flex-col gap-4">
          {paxGroups.map((group, groupIndex) => (
            <Fragment key={group.category}>
              {groupIndex > 0 && <Separator className="border-border" />}

              <span className="text-xs font-bold uppercase text-primary">
                {group.category}
              </span>

              {group.rows.map((row, rowIndex) => (
                <Fragment key={row.key}>
                  {rowIndex > 0 && <Separator className="border-border" />}

                  <StepperRow
                    label={row.label}
                    ageFrom={row.ageFrom}
                    ageTo={row.ageTo}
                    value={draft[row.key]}
                    min={0}
                    max={perTypeLimit}
                    disableIncrement={draft[row.key] >= perTypeLimit}
                    onChange={(next) => update(row.key, next)}
                    onDecrement={() => update(row.key, draft[row.key] - 1)}
                    onIncrement={() => update(row.key, draft[row.key] + 1)}
                  />
                </Fragment>
              ))}
            </Fragment>
          ))}
        </div>
      </div>

      {/* Fixed button */}
      <Button
        type="button"
        variant="default"
        className="w-full"
        onClick={handleDone}
      >
        Add Passenger
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold text-field">{label}</Label>

      {isMobile ? (
        <Drawer open={open} onOpenChange={handleOpenChange} showSwipeHandle>
          <DrawerTrigger render={trigger} />

          <DrawerContent>
            <DrawerClose className="border-b border-border p-4">
              <Button
                variant="outline"
                className="z-50 h-10 w-10 rounded-full"
                onClick={() => setOpen(false)}
              >
                <X size={24} className="text-muted shrink-0" />
              </Button>
            </DrawerClose>

            <div className="p-4">{stepper}</div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger render={trigger} />

          <PopoverContent
            align="start"
            className="w-(--anchor-width) rounded-2xl p-4 shadow-xl md:w-80"
          >
            {stepper}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
